import { Link, useLocation, useNavigate } from "react-router-dom";
import transferIcon from "../assets/images/icons/transfer-icon.svg";
import shareIcon from "../assets/images/icons/share-icon.svg";
import cardTrash from "../assets/images/icons/card-trash.svg";
import threeDotIcon from "../assets/images/icons/threeDotIcon.svg";
import { ReactComponent as CircleCheckIcon } from "../assets/images/icons/tick-circle.svg";
import { useTranslation } from "react-i18next";
import defaultBg from "../assets/images/default-bg.png";
import { DEFAULT_IMAGES, PROFILE_TYPES } from "../constants/constants";
import { useContext, useState } from "react";
import { LoginContext } from "../utils/Auth";

const ProfileCard = ({
  data,
  inBox,
  remove,
  add,
  onClickTransfer,
  onClickShare,
  hideButton,
  customAvatar,
  customBg,
  disabled,
}) => {
  const path = useLocation().pathname;
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isPro = data?.subscription?.tier === PROFILE_TYPES.personal;
  const isBusiness = data?.subscription?.tier === PROFILE_TYPES.business;
  const isBoxDetails = path?.includes("box-details");
  const { user } = useContext(LoginContext);
  const navigate = useNavigate();

  const formatNumber = (number) => {
    const units = [
      t("numberFormats.k"),
      t("numberFormats.m"),
      t("numberFormats.b"),
      t("numberFormats.t"),
    ];
    const delimiter = ",";
    if (isNaN(number)) {
      return "";
    }
    if (number < 1000) {
      return number.toString();
    }
    const absNumber = Math.abs(number);
    let unitIndex = Math.floor(Math.log10(absNumber) / 3);
    let formattedNumber = (number / Math.pow(1000, unitIndex)).toFixed(1);
    if (formattedNumber.endsWith(".0")) {
      formattedNumber = formattedNumber.slice(0, -2);
    }
    if (unitIndex < units.length) {
      formattedNumber += " " + units[unitIndex - 1];
    } else {
      formattedNumber += " " + units[units.length - 1];
    }
    return formattedNumber.replace(".", delimiter);
  };

  const handleCardClick = (e) => {
    if (
      e.target.closest(".cardBtn") ||
      e.target.closest(".profileCardOverlay")
    ) {
      return;
    } else if (e.target.closest(".phone")) {
      return;
    } else {
      navigate(`/profile/${data?.username}`);
    }
  };

  return (
    <div
      className="profileCard"
      onClick={disabled ? () => {} : handleCardClick}
    >
      <div
        className={`profileCardOverlay ${isMenuOpen && "active"}`}
        onClick={() => setIsMenuOpen(false)}
      ></div>
      <div className="cardTop">
        <div className="images">
          <div
            className={`avatar ${isPro && "avatarPro"} ${
              data.avatar === null && "isDefault"
            }`}
          >
            {data.avatar ? (
              <img src={data.avatar} alt={data.full_name} />
            ) : (
              <img
                src={customAvatar ? customAvatar : DEFAULT_IMAGES?.avatar_b64}
                alt="default-icon"
                className="defaultIcon"
              />
            )}
            {isPro && (
              <span
                className={`badge ${
                  data.avatar === null && "defaultBadgePosition"
                }`}
              >
                {t("profileCard.pro")}
              </span>
            )}
          </div>
          <div className="background">
            <img
              src={
                customBg
                  ? customBg
                  : data?.background_image
                    ? data.background_image
                    : defaultBg
              }
              alt="background_image"
            />
          </div>
        </div>
      </div>
      <div className="cardBottom">
        <div className="cardTitle">
          <div className="title">
            <p className="fullName ellipsis">{data.full_name}</p>
            {isBusiness && (
              <span className="badge">{t("profileCard.company")}</span>
            )}
          </div>
          <span className="companyCount">
            {formatNumber(data?.in_boxes_count)} {t("profileCard.saved")}
          </span>
        </div>
        <div className="cardInfo">
          <div className="info">
            {(isPro || isBusiness) && (
              <p className="profession ">
                {data?.profession && `${data?.profession?.name}`}
                <br />
                {/* {data?.profession && `${data?.profession?.description}`} */}
              </p>
            )}
            <p
              className="profession ellipsis"
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
              }}
            >
              {data?.bio}
            </p>
            {!isPro && !isBusiness && (
              <Link className="phone" to={`tel:${data?.phone_number}`}>
                {data?.phone_number}
              </Link>
            )}
          </div>
          {!hideButton && inBox && (
            <button
              className={`cardBtn ${!isBoxDetails && "remove"}`}
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              disabled={!isBoxDetails}
            >
              {isBoxDetails ? (
                <img src={threeDotIcon} alt="three-dot-icon" />
              ) : (
                <CircleCheckIcon />
              )}
              {isBoxDetails ? "" : t("profileCard.added")}
            </button>
          )}
          {!inBox && data?.id !== user?.profile?.id && (
            <button
              className="cardBtn add"
              onClick={(e) => {
                e.stopPropagation();
                add();
              }}
            >
              + {t("profileCard.add")}
            </button>
          )}
          {!inBox && data?.id === user?.profile?.id && (
            <button
              className="cardBtn"
              onClick={() => {
                navigate(`/profile/${user?.username}`);
              }}
            >
              {t("profileCard.view")}
            </button>
          )}
        </div>
      </div>
      <div className={`cardOption ${isMenuOpen && "isOpen"}`}>
        <div className="seperator"></div>
        <div
          className="option"
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(false);
            onClickTransfer();
          }}
        >
          <img src={transferIcon} alt="transferIcon" />
          <span>{t("profileCard.transfer")}</span>
        </div>
        <div
          className="option"
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(false);
            onClickShare();
          }}
        >
          <img src={shareIcon} alt="shareIcon" />
          <span>{t("profileCard.share")}</span>
        </div>
        <div
          className="option"
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(false);
            remove();
          }}
        >
          <img src={cardTrash} alt="cardTrash" />
          <span>{t("profileCard.delete")}</span>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
